import { FirebaseError } from 'firebase/app'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from './firebase'
import {
  DEFAULT_EXECUTIVE_SUMMARY_GOALS,
  type ExecutiveSummaryGoals,
} from '../data/executiveSummaryData'

const GOALS_DOC = doc(db, 'dashboardSettings', 'executiveSummary')
const LOCAL_GOALS_KEY = 'executiveSummaryGoals'

function friendlyGoalsError(err: unknown): string {
  if (err instanceof FirebaseError) {
    if (err.code === 'permission-denied') {
      return 'Permission denied. Sign in with your @bvt.co.nz account.'
    }
    if (err.code === 'unauthenticated') {
      return 'Sign in to save goals.'
    }
  }
  if (err instanceof Error) return err.message
  return 'Failed to save goals'
}

function readLocalGoals(): ExecutiveSummaryGoals | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(LOCAL_GOALS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ExecutiveSummaryGoals>
    return { ...DEFAULT_EXECUTIVE_SUMMARY_GOALS, ...parsed }
  } catch {
    return null
  }
}

function writeLocalGoals(goals: ExecutiveSummaryGoals): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(LOCAL_GOALS_KEY, JSON.stringify(goals))
}

export async function fetchExecutiveSummaryGoals(): Promise<ExecutiveSummaryGoals> {
  try {
    const snap = await getDoc(GOALS_DOC)
    if (snap.exists()) {
      const stored = snap.data().goals as Partial<ExecutiveSummaryGoals> | undefined
      const merged = { ...DEFAULT_EXECUTIVE_SUMMARY_GOALS, ...stored }
      writeLocalGoals(merged)
      return merged
    }
  } catch {
    // Fall through to local fallback.
  }

  return readLocalGoals() ?? { ...DEFAULT_EXECUTIVE_SUMMARY_GOALS }
}

export type SaveExecutiveSummaryGoalsResult = {
  /** Saved to Firestore for all org users */
  shared: boolean
}

export async function saveExecutiveSummaryGoals(
  goals: ExecutiveSummaryGoals,
): Promise<SaveExecutiveSummaryGoalsResult> {
  writeLocalGoals(goals)

  try {
    await setDoc(
      GOALS_DOC,
      { goals, updatedAt: serverTimestamp() },
      { merge: true },
    )
    return { shared: true }
  } catch (err) {
    if (err instanceof FirebaseError && err.code === 'permission-denied') {
      return { shared: false }
    }
    throw new Error(friendlyGoalsError(err))
  }
}
